/**
 * Typed clients for SOP / posture **evaluation** HTTP APIs (browser).
 * Geometry from landmarks stays local per product plan; see docs/features/260605-server-only-sop-posture-hybrid/PLAN.md.
 */

import { apiFetch } from '@/lib/api-client';
import { sanitizeSopFormValuesForApi } from '@/lib/sanitize-sop-form-values-for-api';
import type { Analysis, FormValues } from '@/lib/types';
import type { RuleDocVersion, SopPhaseGoals } from '@/lib/posture-analysis/rule-params';
import type { SopAnalysisResult } from '@/components/sop-intake/sop-analyzer/types';
import type { SectionSummaries } from '@/components/shared/analysis-footer/lib/derivations';
import { measureAsyncStep } from '@/components/posture-analysis/self-assessment/lib/self-assessment-perf';


export async function postAnalyzeSop(
  params: {
    formValues: FormValues;
    ruleDocVersion?: RuleDocVersion;
    postureClassification?: string | null;
  },
  init?: RequestInit
): Promise<SopAnalysisResult> {
  const { formValues, ruleDocVersion = 'current', postureClassification } = params;
  const safeForm = sanitizeSopFormValuesForApi(formValues);
  return apiFetch<SopAnalysisResult>('/assessments/analyze-sop', {
    method: 'POST',
    body: JSON.stringify({
      formValues: safeForm,
      ruleDocVersion,
      postureClassification: postureClassification ?? null
    }),
    ...init
  });
}

export type SopFooterResponse = {
  phaseCode: 'P1' | 'P2' | 'P3';
  overallDisplay: {
    lateralizationLabel: string;
    phaseLabel: string;
    patternLabel?: string;
    fullText: string;
  };
  sectionSummaries: SectionSummaries;
};

export async function postSopFooter(
  params: {
    formValues: FormValues;
    analysis: Analysis | null | undefined;
    clientClassificationCode?: string | null;
    ruleDocVersion?: RuleDocVersion;
    phaseGoals?: Partial<SopPhaseGoals> | null;
    variant?: 'sop' | 'posture';
  },
  init?: RequestInit
): Promise<SopFooterResponse> {
  const {
    formValues,
    analysis,
    clientClassificationCode,
    ruleDocVersion = 'current',
    phaseGoals,
    variant = 'sop'
  } = params;
  const safeForm = sanitizeSopFormValuesForApi(formValues);
  return apiFetch<SopFooterResponse>('/assessments/sop-footer', {
    method: 'POST',
    body: JSON.stringify({
      formValues: safeForm,
      analysis: analysis ?? null,
      clientClassificationCode: clientClassificationCode ?? null,
      ruleDocVersion,
      phaseGoals: phaseGoals ?? null,
      variant
    }),
    ...init
  });
}

export async function postEvaluateSop(
  params: {
    formValues: FormValues;
    phaseGoals?: Partial<SopPhaseGoals> | null;
    analysisThreshold?: number;
    ruleDocVersion?: RuleDocVersion;
  },
  init?: RequestInit
): Promise<Analysis | null> {
  const { formValues, phaseGoals, analysisThreshold, ruleDocVersion = 'current' } = params;
  const safeForm = sanitizeSopFormValuesForApi(formValues);
  return measureAsyncStep('evaluate_sop_api_latency', () =>
    apiFetch<Analysis | null>('/assessments/evaluate-sop', {
      method: 'POST',
      body: JSON.stringify({
        formValues: safeForm,
        phaseGoals: phaseGoals ?? null,
        analysisThreshold,
        ruleDocVersion
      }),
      ...init
    })
  );
}

